// Small UI helpers — formatting, countdowns, scoring categorization.

// Tournament is anchored to US Pacific Time (spec §3 `lock_timezone`). The
// "today / tomorrow" buckets must use PT so a match at 1pm PT (a "Saturday"
// fixture) doesn't fall on a different calendar day for a viewer in India —
// where 1pm PT is 1:30am Sunday IST. Using browser-local time hid the match
// inside "Tomorrow" for non-PT viewers and locked them out of the picker.
const PT_TZ = "America/Los_Angeles";

export function formatLocalTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function formatLocalDay(iso: string): string {
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

// "YYYY-MM-DD" calendar key in Pacific Time for a given instant.
export function ptDateKey(date: Date | number): string {
  const d = typeof date === "number" ? new Date(date) : date;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${day}`;
}

// Tomorrow's PT key. Done via parsed components + Date.UTC so the day rollover
// is correct regardless of the local DST state (noon UTC is mid-morning PT,
// which sits safely inside that PT calendar day).
function tomorrowPtKey(nowPtKey: string): string {
  const [y, m, d] = nowPtKey.split("-").map(Number);
  return ptDateKey(new Date(Date.UTC(y, m - 1, d + 1, 12)));
}

export function isPtToday(iso: string, now: Date | number = new Date()): boolean {
  return ptDateKey(new Date(iso)) === ptDateKey(now);
}

export function isPtTomorrow(iso: string, now: Date | number = new Date()): boolean {
  return ptDateKey(new Date(iso)) === tomorrowPtKey(ptDateKey(now));
}

// Display the PT calendar date for an instant ("Jul 4"), so labels stay in
// sync with the day buckets regardless of viewer timezone.
export function formatPtDate(iso: string | Date | number): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric", timeZone: PT_TZ });
}

// Stable-width live countdown given a target millisecond timestamp.
export function countdown(targetMs: number, nowMs: number): string {
  const totalSeconds = Math.max(0, Math.floor((targetMs - nowMs) / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const mins = Math.floor((totalSeconds % 3_600) / 60);
  const secs = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

// Map points → visual variants using that match's configured maximum.
export function formChipClass(points: number, max: number, missingAward = false): string {
  if (missingAward)       return "form-chip form-chip--miss";
  if (points === max)     return "form-chip form-chip--exact";  // winner + exact score
  if (points > 1)         return "form-chip form-chip--gd";     // winner / strong partial
  if (points > 0)         return "form-chip form-chip--win";    // exact score only
  return                         "form-chip form-chip--miss";   // 0
}

export function pointsChipClass(points: number, max: number): string {
  if (points === max) return "chip chip--gold"; // maximum points
  if (points > 0)     return "chip chip--up";   // partial points
  return                        "chip chip--zero"; // 0
}

// "If the match ended at the current score, how many points would this pick earn?"
// Used to highlight live picks on the prediction wall.
//   - Final / final_pens matches: server already graded; trust `pick.points`.
//   - Live matches: compute on the fly. Winner side is taken from the current
//     leader (no leader during a live draw → no winner credit).
//   - Scheduled (no score yet): null (no highlight).
type ScoredPick = {
  home_score: number;
  away_score: number;
  predicted_winner: "home" | "away" | null;
  points: number | null;
};
type ScoredMatch = {
  status: "scheduled" | "live" | "final" | "final_pens";
  home_score: number | null;
  away_score: number | null;
};
type Scoring = { winner: number; exact: number };

export function liveOrFinalPickPoints(pick: ScoredPick, match: ScoredMatch, scoring: Scoring): number | null {
  if (match.status === "final" || match.status === "final_pens") {
    return pick.points ?? 0;
  }
  if (match.status !== "live") return null;
  const h = match.home_score, a = match.away_score;
  if (h === null || a === null) return null;
  let pts = 0;
  if (pick.home_score === h && pick.away_score === a) pts += scoring.exact;
  const leader = h > a ? "home" : a > h ? "away" : null;
  if (leader && pick.predicted_winner === leader) pts += scoring.winner;
  return pts;
}
